/**
 * Shared session expiration logic for waiter/tablet work sessions.
 *
 * Rules (whichever fires first):
 *  1. 12-hour hard limit — `expires_at` from the database
 *  2. Midnight + 6h — if the session started on a previous day and
 *     the current Brasilia time is past 06:00, the session is stale.
 */

import { getBrasiliaHours, getBrasiliaDate } from "@/lib/date-utils";

/** Returns true when the session should be considered expired. */
export function isSessionExpired(
  expiresAt: string | null,
  startedAt: string | null
): boolean {
  const now = new Date();

  // Rule 1: 12-hour hard limit
  if (expiresAt && new Date(expiresAt) < now) {
    return true;
  }

  // Rule 2: Midnight + 6h (Brasilia time)
  if (startedAt) {
    const startedDate = new Date(startedAt);
    // Convert started_at to Brasilia date string for comparison
    const startedBrasiliaDate = toBrasiliaDateString(startedDate);
    const todayBrasilia = getBrasiliaDate();

    if (startedBrasiliaDate < todayBrasilia && getBrasiliaHours() >= 6) {
      return true;
    }
  }

  return false;
}

/** Convert a Date to YYYY-MM-DD in Brasilia timezone */
function toBrasiliaDateString(date: Date): string {
  const BRASILIA_OFFSET_MINUTES = -3 * 60;
  const utcMs = date.getTime() + date.getTimezoneOffset() * 60000;
  const brasilia = new Date(utcMs + BRASILIA_OFFSET_MINUTES * 60000);
  const y = brasilia.getFullYear();
  const m = String(brasilia.getMonth() + 1).padStart(2, "0");
  const d = String(brasilia.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
